import { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { useTranslation } from "../context/LanguageContext";
import { useUserContext } from "../context/UserContext";
import useEnforceLightMode from "../hooks/useEnforceLightMode";
import usePageBackground from "../hooks/usePageBackground";
import { track } from "../utils/analytics";
import { reportOnboardingStep } from "../services/LifecycleService";
import { destinationAfterScanning } from "../utils/moreStores";
import { ONB_BG, ONB_BORDER, ONB_CTA, ONB_MUTED, ONB_TEXT2, OnboardingScreen } from "../components/onboarding/OnboardingParts";

/**
 * Web port of pinto/app/scanning.tsx, shown right after checkout activates:
 * local deals arrive as each store's next scan completes. It continues on its
 * own after a few seconds; the button only shows that deals are loading.
 */
const AUTO_CONTINUE_MS = 3000;

export default function YoureInPage() {
  useEnforceLightMode();
  usePageBackground(ONB_BG);
  const { t } = useTranslation();
  const { user } = useUserContext();
  const navigate = useNavigate();
  const userRef = useRef(user);
  useEffect(() => {
    userRef.current = user;
  }, [user]);

  const retailers = [
    { key: "walmart", label: "Walmart", count: user?.storeIds?.length ?? 0 },
    { key: "home_depot", label: "Home Depot", count: user?.homeDepotStoreIds?.length ?? 0 },
  ].filter((r) => r.count > 0);

  useEffect(() => {
    reportOnboardingStep("scanning");
    track("onboarding_scanning_shown", { retailers: retailers.map((r) => r.key).join(",") });
    // Plus/Pro plans cover more in-store retailers than onboarding sets up;
    // offer those before the deals.
    const timer = window.setTimeout(() => {
      navigate(destinationAfterScanning(userRef.current), { replace: true });
    }, AUTO_CONTINUE_MS);
    return () => window.clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <OnboardingScreen testId="youre-in">
      <style>{`
        @keyframes youre-in-ring { from { transform: scale(1); opacity: .7 } to { transform: scale(1.7); opacity: 0 } }
        @keyframes youre-in-rise { from { transform: translateY(18px); opacity: 0 } to { transform: none; opacity: 1 } }
      `}</style>
      <div
        className="flex flex-1 flex-col items-center justify-center gap-6 px-6 pb-10"
        style={{ animation: "youre-in-rise 500ms cubic-bezier(0.33,1,0.68,1) both" }}
      >
        <div className="relative flex h-[140px] w-[140px] items-center justify-center" aria-hidden="true">
          {[0, 1.3].map((delay) => (
            <span
              key={delay}
              className="absolute inset-0 rounded-full border-[1.5px]"
              style={{ borderColor: "rgba(124,58,237,0.45)", animation: `youre-in-ring 2.6s ease-out ${delay}s infinite` }}
            />
          ))}
          <div className="flex h-[116px] w-[116px] items-center justify-center rounded-full" style={{ backgroundColor: ONB_CTA }}>
            <img src="/hawk_icon_white.svg" alt="" className="h-[68px] w-[68px] object-contain" />
          </div>
        </div>

        <h1 className="text-center text-[38px] font-extrabold leading-[42px] tracking-[-1.3px]">
          {t("onboarding2.scanning_title_1")}
          <br />
          {t("onboarding2.scanning_title_2")}
        </h1>
        <p className="-mt-2.5 max-w-[300px] text-center text-[15px] font-semibold leading-[21px]" style={{ color: ONB_TEXT2 }}>
          {t("onboarding2.scanning_sub")}
        </p>

        {retailers.length > 0 ? (
          <div className="flex w-full flex-col gap-2 rounded-[18px] border bg-white px-4 py-3.5" style={{ borderColor: ONB_BORDER }}>
            {retailers.map((r) => (
              <div key={r.key} className="flex items-center gap-2.5">
                <span className="h-2 w-2 rounded-full bg-[#059669]" />
                <span className="flex-1 text-sm font-bold">
                  {r.label} · {t(r.count > 1 ? "onboarding2.scanning_stores_n" : "onboarding2.scanning_stores_one", { count: r.count })}
                </span>
                <span className="text-[11px] font-bold" style={{ color: ONB_MUTED }}>{t("onboarding2.scanning_status")}</span>
              </div>
            ))}
          </div>
        ) : null}
      </div>

      <div className="px-6 pb-[max(env(safe-area-inset-bottom),28px)]">
        {/* Not a button: it shows the deals loading, then the screen moves on by itself. */}
        <div
          role="progressbar"
          aria-label={t("onboarding2.scanning_loading")}
          className="flex h-[58px] items-center justify-center gap-2.5 rounded-full text-[18px] font-black text-white"
          style={{ backgroundColor: ONB_CTA, boxShadow: "0 10px 24px rgba(124,58,237,0.45)" }}
        >
          <Loader2 className="h-5 w-5 animate-spin" />
          {t("onboarding2.scanning_loading")}
        </div>
      </div>
    </OnboardingScreen>
  );
}
